import React, { createContext, useContext, ReactNode, ComponentType } from 'react'
import { Studio } from 'Core/Studio'
import {
    TaskAdditionalInfo,
    ContainerDataType,
    ContainerType,
    LinearConversationsDataType,
} from 'Types/Tasks.types'
import { AttributeManager } from 'Core/AttributeCore/AttributeManager'

export type TaskContextType = {
    studio: Studio
    taskAdditionalInfo: TaskAdditionalInfo
    getInputDetails: () => ContainerDataType<
        ContainerType.LinearConversation,
        LinearConversationsDataType
    >
    keyBindingActions: (event: KeyboardEvent) => void
    isReadOnly: () => boolean
    getAttributeManager?: () => AttributeManager | undefined
    taskId: string
}

const TaskContext = createContext<TaskContextType | undefined>(undefined)

interface TaskContextProviderProps {
    children: ReactNode
    value: TaskContextType
}

export const TaskContextProvider: React.FC<TaskContextProviderProps> = ({ children, value }) => {
    return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>
}

export const useTaskContext = (): TaskContextType => {
    const context = useContext(TaskContext)
    if (context === undefined) {
        throw new Error('useTaskContext must be used within a TaskContextProvider')
    }
    return context
}

export function withTaskContext<P extends object>(
    Component: ComponentType<P & { taskContext: TaskContextType }>
): ComponentType<P> {
    return (props: P) => {
        const taskContext = useTaskContext()
        return <Component {...props} taskContext={taskContext} />
    }
}

export default TaskContext
